import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

// We want to start Sentry before anything else, even before importing anything except polyfills.
import './init/init-sentry';

import React from 'react';
import ReactDOM from 'react-dom';

import { init } from './init';
import { App } from './app';

const ROOT_NODE_ID = 'uberall-near-me-check';

// Declare globals so they survive HMR / Strict Mode
declare global {
    interface Window {
        __NMC_MOUNTED?: boolean;
        __NMC_INIT_CALLED?: boolean;
    }
}

// Utility: wait for the container to exist
function waitForRootNode(selector = `#${ROOT_NODE_ID}`, timeoutMs = 5000): Promise<HTMLElement> {
    if (typeof document === 'undefined') {
        return Promise.reject(new Error('near-me-check: document not available (SSR)'));
    }

    const existing = document.querySelector<HTMLElement>(selector);
    if (existing) return Promise.resolve(existing);

    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            observer.disconnect();
            reject(new Error(`near-me-check: ${selector} not found within ${timeoutMs}ms`));
        }, timeoutMs);

        const observer = new MutationObserver(() => {
            const el = document.querySelector<HTMLElement>(selector);
            if (el) {
                clearTimeout(timeout);
                observer.disconnect();
                resolve(el);
            }
        });

        observer.observe(document.documentElement, { childList: true, subtree: true });
    });
}

(async function bootstrap() {
    if (typeof window === 'undefined') return;
    if (window.__NMC_MOUNTED) return; // already mounted once
    window.__NMC_MOUNTED = true;

    const rootNode = await waitForRootNode(`#${ROOT_NODE_ID}`);

    if (window.__NMC_INIT_CALLED) return; // prevent duplicate init
    window.__NMC_INIT_CALLED = true;

    const { props } = await init(rootNode);

    // React 18 safe render (works in both old/new ReactDOM)
    const createRoot = (ReactDOM as any).createRoot;
    if (typeof createRoot === 'function') {
        const root = createRoot(rootNode);
        root.render(<App {...props} />);
    } else {
        ReactDOM.render(<App {...props} />, rootNode);
    }
})().catch((err) => {
    // eslint-disable-next-line no-console
    console.error(err);
});
